import { OperatorPerformanceBucket } from '@hakimo-ui/hakimo/types';
import { scaleLinear } from 'd3-scale';
import { curveMonotoneX, line } from 'd3-shape';
import dayjs from 'dayjs';
import { MouseEvent, useMemo, useState } from 'react';
import { EscalationMarker, TrendTooltip } from './TrendChartParts';
import { METRICS, MetricKey } from './metrics';

const WIDTH = 384;
const HEIGHT = 168;
const MARGIN = { top: 22, right: 8, bottom: 20, left: 24 };
const LINE_METRICS = METRICS.filter((metric) => metric.key !== 'escalated');

interface Props {
  buckets: OperatorPerformanceBucket[];
  resolutionMinutes: number;
}

export function PerformanceTrendChart(props: Props) {
  const { buckets, resolutionMinutes } = props;
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { x, y, yTicks, xTicks, paths } = useMemo(() => {
    const xScale = scaleLinear()
      .domain([0, Math.max(1, buckets.length - 1)])
      .range([MARGIN.left, WIDTH - MARGIN.right]);
    const yScale = scaleLinear()
      .domain([0, Math.max(4, ...buckets.map((bucket) => bucket.handled))])
      .nice(3)
      .range([HEIGHT - MARGIN.bottom, MARGIN.top]);
    const pathFor = (key: MetricKey) =>
      line<OperatorPerformanceBucket>()
        .x((_, i) => xScale(i))
        .y((bucket) => yScale(bucket[key]))
        .curve(curveMonotoneX)(buckets) ?? '';
    return {
      x: xScale,
      y: yScale,
      yTicks: yScale.ticks(3),
      xTicks: buckets
        .map((bucket, i) => ({ i, at: dayjs(bucket.windowStart) }))
        .filter(({ at }) => at.minute() === 0 && at.hour() % 2 === 0),
      paths: LINE_METRICS.map((metric) => ({
        ...metric,
        d: pathFor(metric.key),
      })),
    };
  }, [buckets]);

  if (buckets.length === 0) {
    return null;
  }

  const handleMouseMove = (event: MouseEvent<SVGSVGElement>) => {
    const offsetX =
      event.clientX - event.currentTarget.getBoundingClientRect().left;
    const index = Math.round(x.invert(offsetX));
    setHoverIndex(Math.min(buckets.length - 1, Math.max(0, index)));
  };

  const hovered = hoverIndex === null ? null : buckets[hoverIndex];
  const hoverX = hoverIndex === null ? 0 : x(hoverIndex);

  return (
    <div className="relative">
      <svg
        width={WIDTH}
        height={HEIGHT}
        role="img"
        aria-label={`Handled, resolved and escalated alarms per ${resolutionMinutes} minutes`}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={MARGIN.left}
              x2={WIDTH - MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
              className="stroke-onlight-line-2 dark:stroke-ondark-line-2"
            />
            <text
              x={MARGIN.left - 6}
              y={y(tick)}
              dy="0.32em"
              textAnchor="end"
              className="fill-onlight-text-2 dark:fill-ondark-text-2 text-[10px]"
            >
              {tick}
            </text>
          </g>
        ))}
        {xTicks.map(({ i, at }) => (
          <text
            key={i}
            x={x(i)}
            y={HEIGHT - 4}
            textAnchor={x(i) + 15 > WIDTH ? 'end' : 'middle'}
            className="fill-onlight-text-2 dark:fill-ondark-text-2 text-[10px]"
          >
            {at.format('HH:mm')}
          </text>
        ))}
        {hovered && (
          <line
            x1={hoverX}
            x2={hoverX}
            y1={MARGIN.top}
            y2={HEIGHT - MARGIN.bottom}
            strokeDasharray="3 3"
            className="stroke-onlight-text-3 dark:stroke-ondark-text-3"
          />
        )}
        {paths.map((path) => (
          <path
            key={path.key}
            d={path.d}
            fill="none"
            strokeWidth={2}
            className={path.strokeClass}
          />
        ))}
        {buckets.map((bucket, i) =>
          bucket.escalated > 0 ? (
            <EscalationMarker
              key={bucket.windowStart}
              x={x(i)}
              y={Math.max(8, y(bucket.handled) - 11)}
              count={bucket.escalated}
            />
          ) : null
        )}
      </svg>
      {hovered && (
        <TrendTooltip
          bucket={hovered}
          resolutionMinutes={resolutionMinutes}
          left={hoverX}
          alignLeft={hoverX > WIDTH / 2}
        />
      )}
    </div>
  );
}

export default PerformanceTrendChart;
